import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { Download } from 'lucide-react';
import { Alert } from '../../components/ui';
import { LinkProblem, PageLoading, PublicShell, Section } from '../public-links/PublicShell';
import { exportUrl, getRequestStatus } from './api';
import type { RequestStatus } from './api';
import { useVisitorLanguage } from './locale';

const DOWNLOAD_CLASS =
  'inline-flex min-h-[2.75rem] items-center gap-2 rounded-md bg-[var(--brand-primary)] px-4 py-2 text-sm font-medium text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-primary)]';

/** Request status page: /d/:token (link kind data_request). Shows only the person's own request. */
export function RequestStatusPage() {
  const { token = '' } = useParams();
  return (
    <PublicShell token={token}>
      <StatusLoader token={token} />
    </PublicShell>
  );
}

// The loader sits inside the shell so it reads the visitor's chosen language.
function StatusLoader({ token }: { token: string }) {
  const { vt } = useVisitorLanguage();
  const q = useQuery({ queryKey: ['public-p4', 'request', token], queryFn: () => getRequestStatus(token), retry: false });
  if (q.isPending) return <PageLoading />;
  if (q.isError) return <LinkProblem error={q.error} onRetry={() => void q.refetch()} />;
  const data: RequestStatus = q.data;
  const date = (iso: string) => new Intl.DateTimeFormat('en-GB', { dateStyle: 'long', timeZone: 'UTC' }).format(new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso));
  return (
    <>
      <h1 className="text-2xl font-semibold">{vt('privacy.public.status.title', { reference: data.reference })}</h1>
      <Section>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
          <dt className="text-slate-600">{vt('privacy.public.status.kind')}</dt>
          <dd>{vt(`privacy.public.kind.${data.kind}`)}</dd>
          <dt className="text-slate-600">{vt('privacy.public.status.state')}</dt>
          <dd className="font-medium">{vt(`privacy.public.status.${data.status}`)}</dd>
          <dt className="text-slate-600">{vt('privacy.public.status.received')}</dt>
          <dd>{date(data.receivedAt)}</dd>
          <dt className="text-slate-600">{vt('privacy.public.status.due')}</dt>
          <dd>{date(data.dueOn)}</dd>
        </dl>
        {data.decisionText ? (
          <Alert kind="info">
            <span className="font-medium">{vt('privacy.public.status.decision')}</span> {data.decisionText}
          </Alert>
        ) : null}
        {data.downloadUrl ? (
          <a href={data.downloadUrl} rel="noopener noreferrer" className={DOWNLOAD_CLASS}>
            <Download aria-hidden="true" className="h-5 w-5" />
            {vt('privacy.public.status.download')}
          </a>
        ) : null}
      </Section>
    </>
  );
}

/** Export download page: /x/:token (link kind data_export). The button goes to the server, which answers 302 to the ZIP. */
export function ExportPage() {
  const { token = '' } = useParams();
  return (
    <PublicShell token={token}>
      <ExportBody token={token} />
    </PublicShell>
  );
}

function ExportBody({ token }: { token: string }) {
  const { vt } = useVisitorLanguage();
  return (
    <>
      <h1 className="text-2xl font-semibold">{vt('privacy.public.export.title')}</h1>
      <Section>
        <p>{vt('privacy.public.export.body')}</p>
        <a href={exportUrl(token)} rel="noopener noreferrer" className={DOWNLOAD_CLASS}>
          <Download aria-hidden="true" className="h-5 w-5" />
          {vt('privacy.public.export.button')}
        </a>
      </Section>
    </>
  );
}
